'use client'

import { Component, useSyncExternalStore, type ComponentProps, type ReactNode } from 'react'
import { Canvas } from '@react-three/fiber'

let webglSupport: boolean | undefined

/**
 * Probe once per page load. Old GPUs, blocklisted drivers, disabled hardware
 * acceleration and exhausted context limits all make three.js throw
 * "Error creating WebGL context." inside the Canvas, which took the page down.
 */
function hasWebGL(): boolean {
  if (webglSupport !== undefined) return webglSupport
  try {
    const probe = document.createElement('canvas')
    const gl = probe.getContext('webgl2') ?? probe.getContext('webgl')
    webglSupport = !!gl
    gl?.getExtension('WEBGL_lose_context')?.loseContext()
  } catch {
    webglSupport = false
  }
  return webglSupport
}

const noopSubscribe = () => () => {}

class CanvasBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}

/** Drop-in for R3F's Canvas that renders `fallback` when WebGL is unavailable. */
export function SafeCanvas({ fallback = null, ...props }: ComponentProps<typeof Canvas> & { fallback?: ReactNode }) {
  // Server snapshot is false so SSR'd callers (the agent Orb) hydrate cleanly.
  const supported = useSyncExternalStore(noopSubscribe, hasWebGL, () => false)
  if (!supported) return <>{fallback}</>
  return (
    <CanvasBoundary fallback={fallback}>
      <Canvas {...props} />
    </CanvasBoundary>
  )
}
